import { io } from 'socket.io-client';
import { reportWebError } from 'src/utils/errorTelemetry';

class SocketClient {
  socket;
  connectPromise;
  authToken;
  shopId;
  pendingAuthToken;
  pendingShopId;
  cancelConnect;

  connect(idToken, shopId) {
    const normalizedToken = String(idToken || '').trim();
    const normalizedShopId = Number(shopId || 0);
    if (!normalizedToken) {
      return Promise.reject(new Error('Socket authentication is unavailable.'));
    }

    if (
      this.socket?.connected &&
      this.authToken === normalizedToken &&
      this.shopId === normalizedShopId
    ) {
      return Promise.resolve();
    }

    if (this.connectPromise) {
      if (this.pendingAuthToken === normalizedToken && this.pendingShopId === normalizedShopId) {
        return this.connectPromise;
      }
      return this.connectPromise
        .catch(() => undefined)
        .then(() => this.connect(normalizedToken, normalizedShopId));
    }

    const socketHost = process.env.REACT_APP_SOCKET_PROD_HOST;
    // const socketHost = process.env.REACT_APP_SOCKET_DEV_HOST;

    if (!socketHost) {
      return Promise.reject(new Error('Socket host is not configured.'));
    }

    this.authToken = normalizedToken;
    this.shopId = normalizedShopId;
    if (!this.socket) {
      this.socket = io(socketHost, {
        autoConnect: false,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        auth: { idToken: normalizedToken, shopId: normalizedShopId || undefined }
      });
      this.socket.on('connect_error', (error) => {
        reportWebError(error, {
          source: 'socket',
          errorType: 'SocketConnectionError',
          context: { connected: false }
        });
      });
      this.socket.on('error', (error) => {
        reportWebError(error, {
          source: 'socket',
          errorType: 'SocketError',
          context: { connected: Boolean(this.socket?.connected) }
        });
      });
    } else {
      this.socket.auth = { idToken: normalizedToken, shopId: normalizedShopId || undefined };
      if (this.socket.connected) {
        this.socket.disconnect();
      }
    }

    this.pendingAuthToken = normalizedToken;
    this.pendingShopId = normalizedShopId;
    this.connectPromise = new Promise((resolve, reject) => {
      const onConnect = () => {
        cleanup();
        this.connectPromise = null;
        this.cancelConnect = null;
        this.pendingAuthToken = null;
        this.pendingShopId = null;
        resolve();
      };

      const onConnectError = (error) => {
        cleanup();
        this.connectPromise = null;
        this.cancelConnect = null;
        this.pendingAuthToken = null;
        this.pendingShopId = null;
        reject(error);
      };

      const cleanup = () => {
        this.socket?.off('connect', onConnect);
        this.socket?.off('connect_error', onConnectError);
      };

      this.socket.on('connect', onConnect);
      this.socket.on('connect_error', onConnectError);
      this.cancelConnect = (error) => {
        cleanup();
        this.connectPromise = null;
        this.cancelConnect = null;
        this.pendingAuthToken = null;
        this.pendingShopId = null;
        reject(error);
      };
    });

    this.socket.connect();

    return this.connectPromise;
  }

  waitUntilConnected(timeoutMs = 10000) {
    if (this.socket?.connected) {
      return Promise.resolve();
    }

    if (!this.socket) {
      return Promise.reject(new Error('Socket connection has not been initialized.'));
    }

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        window.clearTimeout(timeoutId);
        this.socket?.off('connect', handleConnect);
        this.socket?.off('connect_error', handleConnectError);
      };

      const handleConnect = () => {
        cleanup();
        resolve();
      };

      const handleConnectError = (error) => {
        cleanup();
        reject(error);
      };

      const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Timed out while connecting to the realtime channel.'));
      }, timeoutMs);

      this.socket.on('connect', handleConnect);
      this.socket.on('connect_error', handleConnectError);
    });
  }

  disconnect() {
    this.cancelConnect?.(new Error('Socket connection cancelled.'));
    if (!this.socket) {
      this.connectPromise = null;
      return Promise.resolve();
    }

    const currentSocket = this.socket;
    this.socket = null;
    this.connectPromise = null;
    this.authToken = null;
    this.shopId = null;

    return new Promise((resolve) => {
      if (!currentSocket.connected) {
        currentSocket.removeAllListeners();
        resolve();
        return;
      }

      currentSocket.once('disconnect', () => {
        currentSocket.removeAllListeners();
        resolve();
      });

      currentSocket.disconnect();
    });
  }

  async emit(event, data) {
    await this.waitUntilConnected();
    if (!this.socket?.connected) {
      throw new Error('Realtime channel disconnected before the action could be sent.');
    }
    this.socket.emit(event, data);
  }

  async emitWithAck(event, data, timeoutMs = 10000) {
    await this.waitUntilConnected(timeoutMs);
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Realtime channel disconnected before the request could be sent.'));
        return;
      }
      this.socket.timeout(timeoutMs).emit(event, data, (error, response) => {
        if (error) {
          reject(new Error('Socket request timed out.'));
          return;
        }
        if (response?.error) {
          reject(new Error(response.error));
          return;
        }
        resolve(response);
      });
    });
  }

  on(event, fun) {
    // No promise is needed here, but we're expecting one in the middleware.
    if (!this.socket) {
      return Promise.reject(new Error('No socket connection.'));
    }

    this.socket.on(event, fun);
    return Promise.resolve();
  }

  off(event, fun) {
    if (!this.socket) {
      return Promise.resolve();
    }

    this.socket.off(event, fun);
    return Promise.resolve();
  }
}

export default SocketClient;
